// Considered: ChipAtom — rejected because StatusBadge needs three visual variants (dot/chip/sparkle) all tied to the same StatusValue color semantics. See spec Reuse Audit.
import React from 'react';
import { useTranslation } from 'localization';
import './StatusBadgeAtom.scss';
import type { StatusValue } from 'types/spherePoints';
import { getStatusColor, getStatusCssVar, getLocalizedStatusLabel } from '../../../utils/statusColors';

export type { StatusValue } from 'types/spherePoints';

export type StatusBadgeVariant = 'dot' | 'chip' | 'sparkle';

interface StatusBadgeProps {
  status: StatusValue;
  variant?: StatusBadgeVariant;
  showLabel?: boolean;
  label?: string;
  className?: string;
  style?: React.CSSProperties;
  size?: number;
  'data-testid'?: string;
}

interface StatusIconProps {
  color: string;
  size?: number;
}

const StatusIcon: React.FC<StatusIconProps> = ({ color, size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 27 26"
    fill="none"
    aria-hidden="true"
  >
    <g clipPath="url(#status-clip)">
      <path
        d="M6.72039 12.8585L11.5481 10.986L13.3037 5.83639C13.4867 5.29933 14.1988 5.29933 14.3819 5.83639L16.1374 10.986L20.9651 12.8585C21.4687 13.0538 21.4687 13.8134 20.9651 14.0087L16.1374 15.8812L14.3819 21.0308C14.1988 21.5679 13.4867 21.5679 13.3037 21.0308L11.5481 15.8812L6.72039 14.0087C6.2169 13.8134 6.2169 13.0538 6.72039 12.8585Z"
        fill={color}
        stroke={color}
      />
    </g>
    <defs>
      <clipPath id="status-clip">
        <rect width="26" height="26" fill="white" transform="translate(26.5) rotate(90)" />
      </clipPath>
    </defs>
  </svg>
);

const StatusBadgeAtom: React.FC<StatusBadgeProps> = ({
  status,
  variant = 'sparkle',
  showLabel = false,
  label,
  className = '',
  style,
  size = 24,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation();
  const resolvedLabel = label ?? getLocalizedStatusLabel(status, t);
  const cssVar = getStatusCssVar(status);

  if (variant === 'dot') {
    return (
      <span
        className={`health-status-badge health-status-badge--dot ${className}`}
        style={style}
        aria-label={resolvedLabel}
        title={resolvedLabel}
        data-testid={dataTestId}
      >
        <span
          className="health-status-badge__dot-circle"
          style={{ backgroundColor: cssVar }}
        />
        {showLabel && (
          <span className="health-status-badge__label">{resolvedLabel}</span>
        )}
      </span>
    );
  }

  if (variant === 'chip') {
    return (
      <span
        className={`health-status-badge health-status-badge--chip ${className}`}
        style={style}
        aria-label={resolvedLabel}
        data-testid={dataTestId}
      >
        <span
          className="health-status-badge__chip-indicator"
          style={{ backgroundColor: cssVar }}
        />
        <span className="health-status-badge__label">{resolvedLabel}</span>
      </span>
    );
  }

  // sparkle (default) — unchanged rendering
  const color = getStatusColor(status);
  return (
    <div
      className={`health-status-badge ${className}`}
      style={{
        ...style,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px',
      }}
      aria-label={resolvedLabel}
      title={resolvedLabel}
      data-testid={dataTestId}
    >
      <StatusIcon color={color} size={size} />
    </div>
  );
};

export default StatusBadgeAtom;
